import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { showToast } from '../../../utils/user/patient/helpers';
import {
  googleLoginPatient,
  googleRegisterPatient
} from '../../../redux/middleware/user/patient/auth/google_auth';

/**
 * Custom hook for Google authentication
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} toast - Toast notification instance
 * @returns {Object} Authentication utilities and state
 */
const useGoogleAuth = (dispatch, toast) => {
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
	 * Handle Google Sign In - used for both login and registration
	 */
  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        // Fetch user info from Google
        const { data: userInfo } = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
          }
        );

        setGoogleUserInfo(userInfo);

        // Prepare data for backend
        const googleData = {
          token: tokenResponse.access_token,
          userInfo
        };

        try {
          // Try login first
          const loginResponse = await dispatch(googleLoginPatient(googleData));
          showToast(toast, 'success', `Welcome back, ${userInfo.name}!`);
          return loginResponse;
        } catch (loginError) {
          // If login fails, try registration
          if (loginError.response?.status === 404) {
            try {
              const registerResponse = await dispatch(
                googleRegisterPatient(googleData)
              );
              showToast(
                toast,
                'success',
								`Welcome to M-TREAT, ${userInfo.name}!`
              );
              return registerResponse;
            } catch (registerError) {
              showToast(
                toast,
                'error',
                registerError.response?.data?.message ||
									'Registration failed. Please try again.'
              );
              throw registerError;
            }
          } else {
            showToast(
              toast,
              'error',
              loginError.response?.data?.message ||
								'Google authentication failed. Please try again.'
            );
            throw loginError;
          }
        }
      } catch (error) {
        console.error('Google sign in error:', error);
        showToast(toast, 'error', 'Google sign in failed');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login error', error);
      showToast(toast, 'error', 'Google login failed.');
    }
  });

  return {
    googleUserInfo,
    loading,
    handleGoogleSignIn
  };
};

export default useGoogleAuth;
